import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../admin/sessionSlice";
import { postTeacherApi } from "./teacherPostsApi";
import { createPosterExternalReport } from "../../functions/posterReport";
import PROPS from "./props";
import "./edit.css";

import mag1 from "./img/mag/1.jpg";
import mag2 from "./img/mag/2.jpg";
import mag3 from "./img/mag/3.jpg";
import mag4 from "./img/mag/4.jpg";
import mag5 from "./img/mag/5.jpg";
import mag6 from "./img/mag/6.jpg";
import bgDesk from "./img/background-web2.jpg";
import bgMob from "./img/background-mob2.jpg";

const MAGS = {
  "1.jpg": mag1,
  "2.jpg": mag2,
  "3.jpg": mag3,
  "4.jpg": mag4,
  "5.jpg": mag5,
  "6.jpg": mag6,
};

const QUOTE_COLORS = {
  "1.jpg": "#000000",
  "2.jpg": "#000000",
  "3.jpg": "#000000",
  "4.jpg": "#ffffff",
  "5.jpg": "#000000",
  "6.jpg": "#000000",
};

const QUOTE_LIMIT = 500;

function readStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (event) => {
      console.error("[composePoster] failed to load image (likely missing CORS headers on the host):", src, event);
      reject(new Error(`Could not load image: ${src}`));
    };
    img.src = src;
  });
}

function wrapLines(ctx, text, maxWidth) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  });
  if (current) lines.push(current);
  return lines;
}

const TEXT_BOX_WIDTH_DEFAULT = 0.82;
const TEXT_BOX_WIDTH_MIN = 0.2;
const TEXT_BOX_WIDTH_MAX = 0.95;

const EXPORT_MAX_DIMENSION = 1600;

async function composePoster(bgSrc, quote, textStyle, stickers, displayWidth) {
  const bg = await loadImage(bgSrc);

  const naturalWidth = bg.naturalWidth || bg.width;
  const naturalHeight = bg.naturalHeight || bg.height;

  console.log("bg natural",bg.naturalWidth,bg.naturalHeight);
  console.log("width hifht",bg.width,bg.height);
  const exportScale = Math.min(1, EXPORT_MAX_DIMENSION / Math.max(naturalWidth, naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(naturalWidth * exportScale);
  canvas.height = Math.round(naturalHeight * exportScale);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bg, 0, 0, canvas.width, canvas.height);
  console.log("this is export-",exportScale);


  for (const sticker of stickers) {
    const img = await loadImage(sticker.src);
    const w = sticker.w * canvas.width;
    const h = sticker.h * canvas.height;
    const cx = (sticker.x + sticker.w / 2) * canvas.width;
    const cy = (sticker.y + sticker.h / 2) * canvas.height;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(((sticker.rotation || 0) * Math.PI) / 180);
    ctx.drawImage(img, -w / 2, -h / 2, w, h);
    ctx.restore();
  }

  const scale = displayWidth ? canvas.width / displayWidth : canvas.width / 1000;
  const fontSize = Math.max(14, Math.round((textStyle.fontSize || 22) * scale));
  ctx.font = `600 ${fontSize}px Azurite, serif`;
  ctx.fillStyle = textStyle.color || "#000000";
  ctx.textBaseline = "top";

  const boxWidth = canvas.width * (textStyle.width || TEXT_BOX_WIDTH_DEFAULT);
  const boxCenterX = (textStyle.x ?? 0.5) * canvas.width;
  const boxLeft = boxCenterX - boxWidth / 2;
  const boxRight = boxCenterX + boxWidth / 2;
  const align = textStyle.align || "center";
  ctx.textAlign = align;
  const drawX = align === "left" ? boxLeft : align === "right" ? boxRight : boxCenterX;

  const lines = wrapLines(ctx, quote, boxWidth);
  const top = (textStyle.y ?? 0.1) * canvas.height;
  lines.forEach((line, i) => {
    ctx.fillText(line, drawX, top + i * (fontSize * 1.35));
  });

  try {
    return canvas.toDataURL("image/jpeg", 0.82);
  } catch (err) {
    console.error(
      "[composePoster] canvas.toDataURL failed — canvas is tainted, meaning the background or a sticker image " +
        "was loaded without a proper CORS response (Access-Control-Allow-Origin) from its host:",
      { bgSrc, stickerSrcs: stickers.map((s) => s.src), err }
    );
    throw new Error("Could not export the poster image (a background or sticker image blocked CORS export).");
  }
}

export default function EditPoster() {
  const navigate = useNavigate();
  const location = useLocation();
  const adminToken = useSelector(selectAdminToken);
  const storedUser = useMemo(() => readStoredUser(), []);
  const userId = storedUser?.userId || storedUser?.userid || storedUser?.id || "";
  const sessionId = storedUser?.sessionId;
  const organizationId = storedUser?.organizationId;

  const navState = location.state || {};
  const [postId, setPostId] = useState(navState.postId || null);
  const [background, setBackground] = useState(navState.background || "1.jpg");
  const [quote, setQuote] = useState(navState.quote || "");
  const [stickers, setStickers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [textStyle, setTextStyle] = useState({
    fontSize: 22,
    color: QUOTE_COLORS[navState.background || "1.jpg"] || "#000000",
    align: "center",
    x: 0.5,
    y: 0.1,
    width: TEXT_BOX_WIDTH_DEFAULT,
  });
  const stageRef = useRef(null);
  const dragRef = useRef(null);

  const magSrc = MAGS[background] || mag1;
  const selected = stickers.find((s) => s.id === selectedId) || null;

  const updateTextStyle = (patch) => setTextStyle((prev) => ({ ...prev, ...patch }));

  useEffect(() => {
    const body = document.body;
    const prev = body.style.backgroundImage;
    const applyBg = () => {
      const mobile = window.matchMedia("(max-width: 700px)").matches;
      body.classList.remove("common-bg");
      body.style.backgroundImage = `url("${mobile ? bgMob : bgDesk}")`;
      body.style.backgroundSize = "100% 100%";
      body.style.backgroundRepeat = "no-repeat";
      body.style.backgroundPosition = "center center";
      body.style.backgroundAttachment = "fixed";
    };
    applyBg();
    window.addEventListener("resize", applyBg);
    return () => {
      window.removeEventListener("resize", applyBg);
      body.style.backgroundImage = prev;
    };
  }, []);

  useEffect(() => {
    if (postId || !adminToken || !userId) return;
    let cancelled = false;
    (async () => {
      const { ok, data } = await postTeacherApi("getTeacherPosts", adminToken, {
        userId,
        sessionId,
        organizationId,
        page: 1,
      });
      if (cancelled || !ok || !data.draft) {
        navigate("/welcome", { replace: true });
        return;
      }
      setPostId(data.draft.id);
      setQuote(data.draft.quote || "");
      setBackground(data.draft.background || "1.jpg");
    })();
    return () => {
      cancelled = true;
    };
  }, [postId, adminToken, userId, sessionId, organizationId, navigate]);

  const addSticker = (src) => {
    const stage = stageRef.current;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    const w = 0.16;
    const h = (w * rect.width) / rect.height;
    const next = {
      id: `sticker-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
      src,
      x: 0.42,
      y: 0.38,
      w,
      h,
      rotation: 0,
    };
    setStickers((prev) => [...prev, next]);
    setSelectedId(next.id);
  };

  const updateSticker = (id, patch) => {
    setStickers((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const removeSticker = (id) => {
    setStickers((prev) => prev.filter((s) => s.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const onStickerPointerDown = (event, sticker, mode = "move") => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedId(sticker.id);
    const stage = stageRef.current;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    dragRef.current = {
      type: "sticker",
      id: sticker.id,
      mode,
      startX: event.clientX,
      startY: event.clientY,
      orig: { ...sticker },
      rect,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onTextPointerDown = (event, mode = "move-text", dxSign = 1) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedId(null);
    const stage = stageRef.current;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    dragRef.current = {
      type: "text",
      mode,
      dxSign,
      startX: event.clientX,
      startY: event.clientY,
      orig: {
        x: textStyle.x,
        y: textStyle.y,
        width: textStyle.width || TEXT_BOX_WIDTH_DEFAULT,
        fontSize: textStyle.fontSize,
      },
      rect,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  useEffect(() => {
    const onMove = (event) => {
      const drag = dragRef.current;
      if (!drag) return;
      const { rect, orig, mode } = drag;
      if (mode === "move") {
        const dx = (event.clientX - drag.startX) / rect.width;
        const dy = (event.clientY - drag.startY) / rect.height;
        const x = Math.min(1 - orig.w, Math.max(0, orig.x + dx));
        const y = Math.min(1 - orig.h, Math.max(0, orig.y + dy));
        updateSticker(drag.id, { x, y });
      } else if (mode === "resize") {
        const dw = (event.clientX - drag.startX) / rect.width;
        const w = Math.min(0.55, Math.max(0.08, orig.w + dw));
        const h = (w * rect.width) / rect.height;
        updateSticker(drag.id, { w, h });
      } else if (mode === "move-text") {
        const dx = (event.clientX - drag.startX) / rect.width;
        const dy = (event.clientY - drag.startY) / rect.height;
        const half = orig.width / 2;
        const x = Math.min(1 - half, Math.max(half, orig.x + dx));
        const y = Math.min(0.92, Math.max(0, orig.y + dy));
        updateTextStyle({ x, y });
      } else if (mode === "resize-text") {
        const dx = ((event.clientX - drag.startX) / rect.width) * (drag.dxSign || 1);
        const width = Math.min(
          TEXT_BOX_WIDTH_MAX,
          Math.max(TEXT_BOX_WIDTH_MIN, orig.width + dx * 2)
        );
        const half = width / 2;
        const x = Math.min(1 - half, Math.max(half, orig.x));
        updateTextStyle({ width, x });
      } else if (mode === "scale-text") {
        const dx = ((event.clientX - drag.startX) / rect.width) * (drag.dxSign || 1);
        const width = Math.min(
          TEXT_BOX_WIDTH_MAX,
          Math.max(TEXT_BOX_WIDTH_MIN, orig.width + dx * 2)
        );
        const half = width / 2;
        const x = Math.min(1 - half, Math.max(half, orig.x));
        const scale = width / orig.width;
        const fontSize = Math.max(10, Math.min(80, Math.round(orig.fontSize * scale)));
        updateTextStyle({ width, x, fontSize });
      }
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  const handleSave = async () => {
    const text = quote.trim();
    console.log("this is post id", postId);
    console.log("this i s text", text);
    if (!text || !postId) {
      console.warn("[handleSave] aborted early: missing", { hasText: !!text, postId });

      return;
    }
     console.log("this before setvae");
    setSaving(true);
    console.log("this after setvae");
    try {
    console.log("this inside try");

      // if (document.fonts?.load) {
      //   await document.fonts.load(`600 22px Azurite`);
          

      // }
        console.log("this after doc load");
      const stageRect = stageRef.current?.getBoundingClientRect();

    console.log("this after getBoundingClientRect");

      console.log("stagerect--",stageRect);
      const dataUrl = await composePoster(magSrc, text, textStyle, stickers, stageRect?.width);

      console.log(`[handleSave] composed poster size: ${(dataUrl.length / 1024).toFixed(0)}KB (base64)`);
      const { ok, data } = await postTeacherApi("saveTeacherPost", adminToken, {
        userId,
        sessionId,
        organizationId,
        postId,
        quote: text,
        image: dataUrl,
      });
      if (!ok || !data.success) {
        await Swal.fire("Error", data.message || "Could not save the poster.", "error");
        return;
      }
      try {
        await createPosterExternalReport({
          storedUser,
          adminToken,
          stageId: data.id || postId,
        });
      } catch (reportErr) {
        console.error("createPosterExternalReport:", reportErr);
      }
      navigate("/welcome", { replace: true });
    } catch (err) {
      console.error("[handleSave] poster upload failed:", err);
      await Swal.fire("Error", err.message || "Could not save the poster.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="edit-page">
        <div className="edit-inner">
          <div className="edit-props" aria-label="Stickers">
            {PROPS.map((src) => (
              <button
                type="button"
                key={src}
                className="edit-prop"
                onClick={() => addSticker(src)}
                title="Add sticker"
              >
                <img src={src} alt="" />
              </button>
            ))}
          </div>

          <div
            className="edit-stage"
            ref={stageRef}
            onPointerDown={() => setSelectedId(null)}
          >
            <img src={magSrc} alt="" draggable={false} />
            <div
              className="edit-quote"
              style={{
                left: `${textStyle.x * 100}%`,
                top: `${textStyle.y * 100}%`,
                width: `${(textStyle.width || TEXT_BOX_WIDTH_DEFAULT) * 100}%`,
                color: textStyle.color,
                fontSize: `${textStyle.fontSize}px`,
                textAlign: textStyle.align,
              }}
              onPointerDown={(event) => onTextPointerDown(event, "move-text")}
            >
              {quote}
              <span
                className="edit-quote-handle edit-quote-handle-e"
                onPointerDown={(event) => onTextPointerDown(event, "resize-text", 1)}
              />
              <span
                className="edit-quote-handle edit-quote-handle-nw"
                onPointerDown={(event) => onTextPointerDown(event, "scale-text", -1)}
              />
              <span
                className="edit-quote-handle edit-quote-handle-ne"
                onPointerDown={(event) => onTextPointerDown(event, "scale-text", 1)}
              />
              <span
                className="edit-quote-handle edit-quote-handle-sw"
                onPointerDown={(event) => onTextPointerDown(event, "scale-text", -1)}
              />
              <span
                className="edit-quote-handle edit-quote-handle-se"
                onPointerDown={(event) => onTextPointerDown(event, "scale-text", 1)}
              />
            </div>
            {stickers.map((sticker) => (
              <div
                key={sticker.id}
                className={`edit-sticker${selectedId === sticker.id ? " is-selected" : ""}`}
                style={{
                  left: `${sticker.x * 100}%`,
                  top: `${sticker.y * 100}%`,
                  width: `${sticker.w * 100}%`,
                  height: `${sticker.h * 100}%`,
                  transform: `rotate(${sticker.rotation || 0}deg)`,
                }}
                onPointerDown={(event) => onStickerPointerDown(event, sticker, "move")}
              >
                <img src={sticker.src} alt="" draggable={false} />
                {selectedId === sticker.id ? (
                  <span
                    className="edit-sticker-handle"
                    onPointerDown={(event) => onStickerPointerDown(event, sticker, "resize")}
                  />
                ) : null}
              </div>
            ))}
          </div>

          <div className="edit-panel">
            <h2>Edit</h2>
            <textarea
              maxLength={QUOTE_LIMIT}
              value={quote}
              onChange={(e) => setQuote(e.target.value.replace(/^\s+/, "").slice(0, QUOTE_LIMIT))}
            />
            <span className="edit-counter">
              {quote.length}/{QUOTE_LIMIT} Characters
            </span>

            <div className="edit-text-controls">
              <div className="edit-text-field">
                <label>Font Size</label>
                <input
                  type="number"
                  min="12"
                  max="60"
                  value={textStyle.fontSize}
                  onChange={(e) => updateTextStyle({ fontSize: Number(e.target.value) || 12 })}
                />
              </div>
              <div className="edit-text-field">
                <label>Color</label>
                <input
                  type="color"
                  value={textStyle.color}
                  onChange={(e) => updateTextStyle({ color: e.target.value })}
                />
              </div>
              <div className="edit-text-field edit-text-field-wide">
                <label>Text Position</label>
                <select
                  value={textStyle.align}
                  onChange={(e) => updateTextStyle({ align: e.target.value })}
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>
            </div>

            {selected ? (
              <div className="edit-sticker-controls">
                <label>
                  Rotate
                  <input
                    type="range"
                    min="-180"
                    max="180"
                    value={selected.rotation || 0}
                    onChange={(e) => updateSticker(selected.id, { rotation: Number(e.target.value) })}
                  />
                </label>
                <button type="button" className="edit-sticker-remove" onClick={() => removeSticker(selected.id)}>
                  Remove sticker
                </button>
              </div>
            ) : (
              <p className="edit-hint">Drag your text or a sticker on the poster to reposition it.</p>
            )}

            <button type="button" className="edit-save" disabled={saving || !quote.trim() || !postId} onClick={handleSave}>
              {saving ? "Uploading" : "Upload"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
